import { Dispatch } from "react";
import { action, ActionType } from "typesafe-actions";
import { request } from "app/utils/request";
import config from "config/environment";
import { WorkflowModel } from "app/models";

// --- Transform helpers ---

/** Convert raw API snake_case response → camelCase WorkflowModel */
function transformWorkflow(raw: any): WorkflowModel {
  // The request middleware (successHandler → serialize) auto-camelizes all keys
  // before this function receives the response, so use camelCase property names.
  return {
    object: raw.object,
    id: raw.id,
    name: raw.name,
    enabled: raw.enabled,
    activationToken: raw.activationToken,
    acceptTerms: raw.acceptTerms,
    createSpace: raw.createSpace,
    addToList: raw.addToList,
    addPosts: raw.addPosts,
    addMembers: raw.addMembers,
    assignOffering: raw.assignOffering,
    createSpaceSport: raw.createSpaceSport,
    addToListId: raw.addToListId,
    assignOfferingId: raw.assignOfferingId,
    spaceTemplateId: raw.spaceTemplateId,
    qrToken: raw.qrToken,
    qrUrl: raw.qrUrl,
    urlToken: raw.urlToken,
    termsDocuments: raw.termsDocuments,
    facilityId: raw.facilityId,
    facilityName: raw.facilityName,
    coachId: raw.coachId,
    calendarEnabled: raw.calendarEnabled,
    permissions: raw.permissions,
    createdAt: raw.createdAt,
  };
}

/** Convert camelCase WorkflowModel params → snake_case API body */
function workflowParamsToApi(params: Record<string, any>): Record<string, any> {
  const FIELD_MAP: Record<string, string> = {
    name: "name",
    enabled: "enabled",
    activationToken: "activation_token",
    acceptTerms: "accept_terms",
    createSpace: "create_space",
    addToList: "add_to_list",
    addPosts: "add_posts",
    addMembers: "add_members",
    assignOffering: "assign_offering",
    createSpaceSport: "create_space_sport",
    addToListId: "add_to_list_id",
    assignOfferingId: "assign_offering_id",
    spaceTemplateId: "space_template_id",
    qrToken: "qr_token",
    qrUrl: "qr_url",
    urlToken: "url_token",
    termsDocuments: "terms_documents",
    // Write-only param (not a model field — used when saving terms document IDs)
    termsDocumentIds: "terms_document_ids",
    facilityId: "facility_id",
    facilityName: "facility_name",
    coachId: "coach_id",
    calendarEnabled: "calendar_enabled",
  };

  const result: Record<string, any> = {};
  for (const [camel, snake] of Object.entries(FIELD_MAP)) {
    if (camel in params) {
      result[snake] = params[camel];
    }
  }
  return result;
}

// List actions
const listPending = () => action("@workflow.list.pending");
const listError = (err: string) => action("@workflow.list.error", err);
const listSuccess = (data: WorkflowModel[]) =>
  action("@workflow.list.success", data);

// Single fetch actions
const fetchPending = () => action("@workflow.fetch.pending");
const fetchError = (err: string) => action("@workflow.fetch.error", err);
const fetchSuccess = (data: WorkflowModel) =>
  action("@workflow.fetch.success", data);

// Mutate actions
const createPending = () => action("@workflow.create.pending");
const createError = (err: string) => action("@workflow.create.error", err);
const createSuccess = (data: WorkflowModel) =>
  action("@workflow.create.success", data);

const updatePending = () => action("@workflow.update.pending");
const updateError = (err: string) => action("@workflow.update.error", err);
// Update returns 204 No Content, so we carry the id + params for optimistic update
const updateSuccess = (data: { id: string; params: Partial<WorkflowModel> }) =>
  action("@workflow.update.success", data);

const deletePending = () => action("@workflow.delete.pending");
const deleteError = (err: string) => action("@workflow.delete.error", err);
const deleteSuccess = (id: string) => action("@workflow.delete.success", id);

export const clearWorkflow = () => action("@workflow.clear");

export type WorkflowAction = ActionType<
  | typeof listPending
  | typeof listError
  | typeof listSuccess
  | typeof fetchPending
  | typeof fetchError
  | typeof fetchSuccess
  | typeof createPending
  | typeof createError
  | typeof createSuccess
  | typeof updatePending
  | typeof updateError
  | typeof updateSuccess
  | typeof deletePending
  | typeof deleteError
  | typeof deleteSuccess
  | typeof clearWorkflow
>;

export function fetchWorkflows(params: {
  facilityId?: string;
  coachId?: string;
}) {
  return (dispatch: Dispatch<WorkflowAction>) => {
    dispatch(listPending());

    const queryParts: string[] = [];
    if (params.facilityId) {
      queryParts.push(`facility_id=${params.facilityId}`);
    }
    if (params.coachId) {
      queryParts.push(`coach_id=${params.coachId}`);
    }
    const query = queryParts.length ? `?${queryParts.join("&")}` : "";

    const url = config.API_SERVER + `/api/v1/workflows${query}`;
    const promise = request(url, "GET", {}, { version: 1 });
    promise.then((res: any[]) =>
      dispatch(listSuccess(res.map(transformWorkflow))),
    );
    promise.catch((err) => dispatch(listError(err)));

    return promise;
  };
}

export function fetchWorkflow(id: string) {
  return (dispatch: Dispatch<WorkflowAction>) => {
    dispatch(fetchPending());

    const url = config.API_SERVER + `/api/v1/workflows/${id}`;
    const promise = request(url, "GET", {}, { version: 1 });
    promise.then((res: any) => dispatch(fetchSuccess(transformWorkflow(res))));
    promise.catch((err) => dispatch(fetchError(err)));

    return promise;
  };
}

export function createWorkflow(params: {
  facilityId?: string;
  coachId?: string;
  name?: string;
}) {
  return (dispatch: Dispatch<WorkflowAction>) => {
    dispatch(createPending());

    const queryParts: string[] = [];
    if (params.facilityId) {
      queryParts.push(`facility_id=${params.facilityId}`);
    }
    if (params.coachId) {
      queryParts.push(`coach_id=${params.coachId}`);
    }
    const query = queryParts.length ? `?${queryParts.join("&")}` : "";

    const url = config.API_SERVER + `/api/v1/workflows${query}`;
    const promise = request(
      url,
      "POST",
      { workflow: { name: params.name } },
      { version: 1 },
    );
    promise.then((res: any) =>
      dispatch(createSuccess(transformWorkflow(res))),
    );
    promise.catch((err) => dispatch(createError(err)));

    return promise;
  };
}

export function updateWorkflow(
  id: string,
  params: Partial<WorkflowModel>,
) {
  return (dispatch: Dispatch<WorkflowAction>) => {
    dispatch(updatePending());

    const apiParams = workflowParamsToApi(params as Record<string, any>);
    const url = config.API_SERVER + `/api/v1/workflows/${id}`;
    const promise = request(
      url,
      "PUT",
      { workflow: apiParams },
      { version: 1 },
    );
    // API returns 204 No Content — apply optimistic update, then re-fetch
    // to pick up server-generated fields (e.g. spaceTemplateId)
    promise.then(() => {
      dispatch(updateSuccess({ id, params }));

      const fetchUrl = config.API_SERVER + `/api/v1/workflows/${id}`;
      request(fetchUrl, "GET", {}, { version: 1 })
        .then((res: any) => dispatch(fetchSuccess(transformWorkflow(res))))
        .catch(() => {
          // Silent — optimistic update already applied
        });
    });
    promise.catch((err) => dispatch(updateError(err)));

    return promise;
  };
}

export function deleteWorkflow(id: string) {
  return (dispatch: Dispatch<WorkflowAction>) => {
    dispatch(deletePending());

    const url = config.API_SERVER + `/api/v1/workflows/${id}`;
    const promise = request(url, "DELETE", {}, { version: 1 });
    // API returns 204 No Content
    promise.then(() => dispatch(deleteSuccess(id)));
    promise.catch((err) => dispatch(deleteError(err)));

    return promise;
  };
}

// Space template fetch for workflow (requires workflow_id for hidden templates)
export function fetchWorkflowTemplate(
  spaceTemplateId: string,
  workflowId: string,
) {
  return () => {
    const url =
      config.API_SERVER +
      `/api/v1/space_templates/${spaceTemplateId}?workflow_id=${workflowId}`;
    return request(url, "GET", {}, { version: 1 });
  };
}

// Space template member update (separate from workflow CRUD)
// Body is pre-stringified to prevent the serialization layer from stripping
// empty arrays (parseData returns undefined for []), which causes a
// parameter_missing error when removing the last member.
export function updateWorkflowMembers(
  spaceTemplateId: string,
  workflowId: string,
  memberIds: string[],
) {
  return () => {
    const url =
      config.API_SERVER +
      `/api/v1/space_templates/${spaceTemplateId}?workflow_id=${workflowId}`;
    return request(
      url,
      "PUT",
      JSON.stringify({ space_template: { member_ids: memberIds } }) as any,
      { version: 1 },
    );
  };
}
